import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Typography,
  Grid,
  Paper,
  Button,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { AssessmentData, Assessment } from '../../../types/AssessmentTypes';
import LoadingSpinner from '../../../support/LoadingSpinner';
import axios from '../../../../services/ApiService';
import toast from 'react-hot-toast';
import Chip from '@mui/material/Chip';
import { BVTConstants } from '../../../../utils/BVTConstants';
import { ScoreMeter } from '../../../layout/overview/score-meter';

interface AssessmentDetailViewProps {
  userType: 'BUSINESS' | 'AGENT' | 'COMPANY' | 'ADMIN';
}

function AssessmentDetailView({ userType }: AssessmentDetailViewProps) {
  const [userAssessmentData, setUserAssessmentData] = useState<AssessmentData | null>(null);
  const [assessment, setAssessment] = useState<Assessment | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { trackerId } = location.state || {};

  useEffect(() => {
    if (trackerId) {
      fetchAssessmentData(trackerId);
    }
  }, [trackerId]);

  const fetchAssessmentData = async (trackerId: number) => {
    try {
      setLoading(true);
      const response = await axios.get(`/getAssessmentData/${trackerId}`);
      setUserAssessmentData(response.data);
      await fetchAssessment(response.data.assessmentId);
    } catch (error) {
      console.error('Error fetching assessment data.');
    } finally {
      setLoading(false);
    }
  };

  const fetchAssessment = async (assessmentId: number) => {
    try {
      setLoading(true);
      const response = await axios.post("/getAssessment", assessmentId);
      setAssessment(response.data);
    } catch (error) {
      console.error('Error fetching assessment.');
    } finally {
      setLoading(false);
    }
  };

  if (!userAssessmentData || !assessment) return <LoadingSpinner loading={loading} />;

  const resultColorMap: Record<string, string> = {
    [BVTConstants.SCORE_RANGE_POOR]: '#F44336',
    [BVTConstants.SCORE_RANGE_NEED_IMPROVEMENT]: '#FF9800',
    [BVTConstants.SCORE_RANGE_STRONG]: '#8BC34A',
    [BVTConstants.SCORE_RANGE_EXCELLENT]: '#4CAF50',
  };

  return (
    <>
      <Helmet>
        <title>{`BVT ${userType} - Assessment Details`}</title>
      </Helmet>
      <Box sx={{ padding: 3 }}>
        <Card>
          <CardHeader
            title={assessment.name}
            subheader={
              <Box sx={{ mt: 1 }}>
                <Typography variant="h6" gutterBottom>
                  Final Score: {userAssessmentData.scoredPoints} / {userAssessmentData.totalScore}
                  {userAssessmentData.result && (
                    <Chip
                      label={userAssessmentData.result}
                      sx={{
                        ml: 1,
                        backgroundColor: resultColorMap[userAssessmentData.result as keyof typeof resultColorMap],
                        color: '#fff',
                        fontWeight: 600,
                      }}
                      size="small"
                    />
                  )}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Completed on: {new Date(userAssessmentData.completedDate || '').toLocaleDateString()}
                </Typography>
              </Box>
            }
            action={
              userAssessmentData.status === 'COMPLETED' && userAssessmentData.result && userAssessmentData.totalScore > 0 ? (
                <ScoreMeter
                  label=""
                  score={Math.round((userAssessmentData.scoredPoints / userAssessmentData.totalScore) * 100).toString()}
                  scoreRange={userAssessmentData.result}
                  sx={{ width: 240, minWidth: 240, height: 160}}
                />
              ) : null
            }
            sx={{ backgroundColor: '#f5f5f5', pb: 2 }}
          />
          <Divider />
          <CardContent>
            <Typography variant="body1" paragraph sx={{ mb: 4 }}>
              {assessment.description}
            </Typography>

            {assessment.subCategories.map((subCategory) => (
              <Card key={subCategory.id} sx={{ mb: 2 }}>
                <CardHeader
                  title={subCategory.title}
                  sx={{ 
                    backgroundColor: '#e8e8e8',
                    color: '#2f2f2f',
                    py: 1,
                    '& .MuiCardHeader-title': {
                      fontSize: '1.1rem',
                      fontWeight: 600
                    }
                  }}
                />
                <CardContent sx={{ p: 0 }}>
                  {subCategory.questions.map((question, qIndex) => {
                    const answer = userAssessmentData.answers.find(
                      a => a.questionId === question.id
                    );
                    const selectedOption = question.options.find(
                      o => o.id === answer?.selectedOptionId
                    );

                    return (
                      <Box 
                        key={question.id} 
                        sx={{ 
                          p: 2,
                          backgroundColor: qIndex % 2 === 0 ? '#fff' : '#f8f9fa',
                          borderBottom: '1px solid',
                          borderColor: 'divider'
                        }}
                      >
                        <Grid container spacing={1}>
                          <Grid item xs={12}>
                            <Typography 
                              variant="subtitle1" 
                              sx={{ fontWeight: 500, mb: 1 }}
                            >
                              Q{qIndex + 1}. {question.text}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Paper 
                              sx={{ 
                                p: 1.5,
                                backgroundColor: 'success.50',
                                color: 'success.900',
                                border: 1,
                                borderColor: 'success.100'
                              }}
                            >
                              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                Selected Answer
                              </Typography>
                              <Typography variant="body2">
                                {selectedOption?.label || 'Not answered'}
                              </Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Paper 
                              sx={{ 
                                p: 1.5,
                                backgroundColor: 'info.50',
                                color: 'info.900',
                                border: 1,
                                borderColor: 'info.100'
                              }}
                            >
                              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                Score
                              </Typography>
                              <Typography variant="body1">
                                {answer?.score || 0} points
                              </Typography>
                            </Paper>
                          </Grid>
                        </Grid>
                      </Box>
                    );
                  })}
                </CardContent>
              </Card>
            ))}
          </CardContent>
          <Divider />
          <Box sx={{ p: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => navigate(-1)}
            >
              Back
            </Button>
          </Box>
        </Card>
      </Box>
    </>
  );
}

export default AssessmentDetailView; 